import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Flex,
  Text,
  Grid,
  Box,
  Button,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  Skeleton,
  Card,
  useBreakpointValue,
} from "@chakra-ui/react";
import { DownloadIcon, SearchIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { PieChart } from "./PieChart";
import DashboardTable from "./DashboardTable";
import Datepicker from "../common/DatePicker";
import MultiDropdown from "components/common/multiDropdown/MultiDropdown";
import WorkflowListModal from "./WorkflowListModal";
import CustomDateRangePicker from "../common/CustomDateRangePicker/CustomDateRangePicker";
import { HiOutlineRefresh } from "react-icons/hi";
import DashWidget from "./element/DashWidget";
import CustomDateRangeMobile from "../common/CustomDateRangeMobile/CustomDateRangeMobile";
import { routePath } from "config/pathConstant";
import { FaFileUpload } from "react-icons/fa";
import UploadDocumentModal from "../common/UploadDocumentModal/UploadDocumentModal";
import { addOneDayToDate } from "utils/dateUtils/dateUtils";
import WorkflowDocumentCount from "./WorkflowDocumentCount";
import DownloadReportElement from "./downloadReportElement/DownloadReportElement";
import ForeignProcess from "../foreignProcess/ForeignProcess";

export default function Dashboard(): JSX.Element {
  const [showWorkflowDocument, setOpenWorkflowDocument] = useState(false);
  const {
    toastNotify,
    accountDetails,
    workFlowData,
    defaultAbsent,
    activeWorkflow,
    setActivePage,
    getDefaultWorkFlow,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    resetDates,
    workflowProcess
  } = useContext(AppContext);

  const getCurrentTab = () => {
    if (accountDetails.level === "admin") return 0;
    if (accountDetails?.level) {
      const userLevelIndex = parseInt(accountDetails.level.split("-")[1], 10);
      return userLevelIndex - 1;
    }
    return 0;
  };
  const [exceptionalCount, setExceptionalCount] = useState(0);
  const [chartData, setChartData] = useState<any>([]);
  const [approvedData, setApprovedData] = useState<any>(null);
  const [rejectedData, setRejectedData] = useState<any>(null);
  const [duplicateDocument, setDuplicateDocument] = useState<any>(0);
  const [pendingData, setPendingData] = useState<any>(null);
  const [initialData, setInitalialData] = useState<any>(null);
  const [tabIndex, setTabIndex] = useState<number>(getCurrentTab());
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<any>({});
  const [transformedOptions, setTransformedOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [transformedTab, setTransformedTab] = useState<any>([]);
  const [firstTimeCall, setFirstTimeCall] = useState(1);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [userLevelIndex, setUserLevelIndex] = useState<any>();
  const [openForeignDrawer, setOpenForeignDrawer] = useState(false);
  const fileInputRef = useRef<any>(null);
  const isBreakpoint = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (defaultAbsent === true) setIsOpen(true);
  }, [defaultAbsent]);

  useEffect(() => {
    localStorage.removeItem("documentTab");
  }, []);

  useEffect(() => {
    if (accountDetails) {
      if (accountDetails.level !== "admin" && accountDetails?.level) {
        const levelNum = parseInt(accountDetails.level.split("-")[1], 10);
        handleChangeTabIndex(levelNum - 1);
        setUserLevelIndex(levelNum);
      } else {
        handleChangeTabIndex(0);
        setUserLevelIndex(0);
      }
    }
  }, [accountDetails]);

  const handleCloseModal = () => {
    setIsOpen(false);
    setLoading(false);
  };

  const arrangeData = (chartData: any) => {
    const statusOrder: any = { approved: 1, pending: 2, rejected: 3 };
    return chartData.sort((a: any, b: any) => {
      return (
        (statusOrder[a.status] || Infinity) -
        (statusOrder[b.status] || Infinity)
      );
    });
  };

  const calculatesChartData = (data: any) => {
    let pendingTotal = 0;
    let rejectedTotal = 0;
    let approvedTotal = 0;

    console.log("the count data are", data);

    if (data && accountDetails.level) {
      const levelData = data[accountDetails.level];
      pendingTotal = levelData?.pending || 0;
      rejectedTotal = levelData?.rejected || 0;
      approvedTotal = levelData?.approved || 0;
    }

    const result = [
      { status: "pending", count: pendingTotal },
      { status: "rejected", count: rejectedTotal },
      { status: "approved", count: approvedTotal },
    ];

    setDuplicateDocument(data?.duplicates?.total || 0);
    setApprovedData(approvedTotal);
    setPendingData(pendingTotal);
    setRejectedData(rejectedTotal);
    return arrangeData(result);
  };

  const handleChangeTabIndex = (index: number) => {
    setTabIndex(index);
    if (!initialData) return;

    let levelKey =
      accountDetails.level === "admin"
        ? `level-${index}`
        : `level-${index + 1}`;
    const levelData = initialData[levelKey] || {
      pending: 0,
      approved: 0,
      rejected: 0,
    };

    const chartData = Object.keys(levelData).map((status) => ({
      status,
      count: levelData[status],
    }));

    setChartData(arrangeData(chartData));
  };

  const calculateDateFrom = () => {
    const dateRange = workFlowData?.dateFilter?.range;
    const today = new Date();
    const monthsBack = dateRange ? parseInt(dateRange.split("-")[1], 10) : 6;
    today.setMonth(today.getMonth() - monthsBack);
    return today;
  };

  const handleStartDateChange = (date: Date) => {
    setFirstTimeCall(2);
    setStartDate(new Date(date));
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
  };

  const resetFilters = () => {
    setSearchFilters([]);
    setSelectedOptions({});
    resetDates();
    setFirstTimeCall(1);
    getLevelData(true);
  };

  const getLevelData = async (reset?: any) => {
    try {
      setLoading(true);
      let query: any = {
        dateTo: firstTimeCall === 1 ? endDate : addOneDayToDate(endDate),
        dateFrom: startDate,
        level: accountDetails.level,
        _id: accountDetails.user,
        company: accountDetails.company?._id || accountDetails.company,
      };

      if (reset) {
        setFirstTimeCall(1);
        query.dateTo = new Date();
        query.dateFrom = calculateDateFrom();
      }

      if (workFlowData.dateFilter?.filterKey?.length) {
        query.dateFilterKey = workFlowData.dateFilter.filterKey;
      }

      const filters: any[] = [];
      for (const key in selectedOptions) {
        if (Array.isArray(selectedOptions[key])) {
          selectedOptions[key].forEach((item: any) =>
            filters.push({ [key]: item.value })
          );
        } else if (selectedOptions[key]?.value) {
          filters.push({ [key]: selectedOptions[key].value });
        }
      }
      if (filters.length && !reset) query.searchFilters = filters;

      if (activeWorkflow !== accountDetails.defaultWorkflow) {
        query.activeWorkflow = activeWorkflow;
      }

      const data: any = await appRequest("document", "getCount", query);

      if (data?.status === "error") {
        toastNotify({
          title: "Error",
          message: "Something went wrong",
          type: "error",
        });
        return;
      }

      if (Array.isArray(data)) {
        const exceptionalItem = data.find(
          (item) => item?._id?.level === "exceptional"
        );
        setExceptionalCount(exceptionalItem?.count || 0);

        const groupedData = data.reduce((acc: any, item: any) => {
          const key = item._id?.level?.toLowerCase();
          if (!acc[key]) acc[key] = {};
          acc[key][item._id.status] =
            (acc[key][item._id.status] || 0) + item.count;
          return acc;
        }, {});

        setChartData(calculatesChartData(groupedData));
        setInitalialData(groupedData);
      }
    } catch (error: any) {
      toastNotify({
        title: "Error",
        message: error.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
      setSearchFilters([]);
    }
  };

  // Only load on mount or workflow change — NOT on date change
  useEffect(() => {
    if (activeWorkflow && Object.keys(workFlowData).length > 0) {
      getLevelData();
    }
  }, [activeWorkflow, workFlowData]);

  useEffect(() => {
    if (workFlowData?.filters) {
      const activeFilters = workFlowData.filters.filter((f: any) => f.isActive);
      const options = activeFilters.map((filter: any) => ({
        label: filter.label,
        placeholder: filter.placeholder,
        isMultiSelect: filter.isMultiSelect,
        key: filter.key,
        options: filter.filters
          .filter((nf: any) => nf.isActive)
          .map((nf: any) => ({ _id: nf.key, value: nf.key, label: nf.label })),
      }));
      setTransformedOptions(options);
    }
  }, [workFlowData]);

  useEffect(() => {
    if (workFlowData?.approvals) {
      const approvals = workFlowData.approvals.map((a: any) => ({
        ...a,
        level: a.level,
      }));
      let levels = approvals.filter((a: any) => a.level !== "admin");
      levels.sort((a: any, b: any) => a.level.localeCompare(b.level));

      const admin = approvals.find((a: any) => a.level === "admin");
      if (admin && accountDetails.level === "admin") levels.unshift(admin);

      const tabNames = levels
        .filter(
          (a: any, i: number, arr: any[]) =>
            arr.findIndex((x) => x.level === a.level) === i
        )
        .map((a: any) => a.designation || a.level);

      setTransformedTab(tabNames);
    }
  }, [workFlowData, accountDetails]);

  const handleDropdownChange = (value: any, label: string) => {
    setSelectedOptions((prev: any) => ({ ...prev, [label]: value }));
  };

  const handleSearchChange = (value: string) => setSearch(value);

  const handleApply = () => getLevelData();

  const handleSearchClick = () => {
    setFirstTimeCall(2);
    getLevelData();
  };

  const handleOpenUploadModal = () => setIsUploadOpen(true);
  const handleCloseUploadModal = () => setIsUploadOpen(false);




  return (
    <>
      <Grid gap={3} p="0">
        <Box>
          <Box flexWrap="wrap" mb="1.2rem" gap={{ base: 6, lg: 8 }}>
            {!workFlowData?.dateFilter?.rangePicker && (
              <Box display={{ base: "block", md: "none" }} mb={2}>
                <Flex gap={2} justifyContent="flex-end">
                  <Datepicker
                    label="From"
                    date={startDate}
                    onDateChange={setStartDate}
                  />
                  <Datepicker
                    label="To"
                    date={endDate}
                    onDateChange={setEndDate}
                  />
                </Flex>
              </Box>
            )}

            {isBreakpoint &&
              accountDetails?.level === "level-1" &&
              accountDetails?.role === "uploader" && (
                <Flex justifyContent="space-between" my="0.5rem">
                  <Button
                    bg="teal.300"
                    color="white"
                    _hover={{ bg: "teal.400" }}
                    onClick={handleOpenUploadModal}
                  >
                    {isBreakpoint ? <FaFileUpload /> : "Upload Document"}
                  </Button>
                </Flex>
              )}

            <Flex
              gap={{ base: 2, md: 4 }}
              justifyContent={"space-between"}
              alignItems="center"
            >
              {/* {!isBreakpoint &&
                accountDetails?.level === "level-1" &&
                accountDetails?.role === "uploader" && (
                  <Button
                    bg="teal.300"
                    color="white"
                    _hover={{ bg: "teal.400" }}
                    onClick={handleOpenUploadModal}
                  >
                    Upload Document
                  </Button>
                )} */}

              {workflowProcess?.processName === "foreign" && accountDetails?.level === "level-1" ? <Button
                bg="teal.300"
                color="white"
                _hover={{ bg: "teal.400" }}
                onClick={() => setOpenForeignDrawer(true)}
              >
                {"Initiate Document"}
              </Button> : <Box></Box>}
              <Flex gap={4} alignItems="center">
                {workFlowData?.dateFilter?.showFilter && (
                  <>
                    {workFlowData.dateFilter.rangePicker ? (
                      <>
                        <Box display={{ base: "none", lg: "block" }}>
                          <CustomDateRangePicker
                            onStartDateChange={handleStartDateChange}
                            onEndDateChange={handleEndDateChange}
                            dateFrom={startDate}
                            dateTo={endDate}
                            months={2}
                          />
                        </Box>
                        <Box
                          display={{ base: "block", lg: "none" }}
                          w="12.5rem"
                        >
                          <CustomDateRangeMobile
                            startDate={startDate}
                            endDate={endDate}
                            onStartDateChange={handleStartDateChange}
                            onEndDateChange={handleEndDateChange}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box display={{ base: "none", md: "block" }}>
                        <Flex gap={2}>
                          <Datepicker
                            label="From"
                            date={startDate}
                            onDateChange={setStartDate}
                          />
                          <Datepicker
                            label="To"
                            date={endDate}
                            onDateChange={setEndDate}
                          />
                        </Flex>
                      </Box>
                    )}
                  </>
                )}

                {workFlowData?.dateFilter?.showFilter && (
                  <Tooltip hasArrow label="Search" placement="left">
                    <Button
                      bg="teal.300"
                      color="white"
                      _hover={{ bg: "teal.400" }}
                      onClick={handleSearchClick}
                    >
                      <SearchIcon />
                    </Button>
                  </Tooltip>
                )}

                <Tooltip hasArrow label="Reset" placement="left">
                  <Button
                    border="2px solid"
                    borderColor="teal.300"
                    bg="white"
                    color="teal.400"
                    _hover={{ bg: "teal.300", color: "white" }}
                    onClick={resetFilters}
                  >
                    <HiOutlineRefresh fontSize="1.2rem" />
                  </Button>
                </Tooltip>
                <DownloadReportElement />
                {workFlowData?.filters?.filter((f: any) => f.isActive).length >
                  0 && (
                    <Box display={{ base: "none", md: "block" }}>
                      <MultiDropdown
                        search={{ visible: true, placeholder: "select type" }}
                        selectedOptions={selectedOptions}
                        onDropdownChange={handleDropdownChange}
                        dropdowns={transformedOptions}
                        onApply={handleApply}
                        searchValue={search}
                        onSearchChange={handleSearchChange}
                        resetFilters={resetFilters}
                        dateFrom={startDate}
                        dateTo={endDate}
                        setFromDate={setStartDate}
                        setToDate={setEndDate}
                        handleGet={getLevelData}
                        isTable={false}
                      />
                    </Box>
                  )}
              </Flex>
            </Flex>

            {workFlowData?.filters?.filter((f: any) => f.isActive).length >
              0 && (
                <Box
                  position="fixed"
                  top="15rem"
                  right="1rem"
                  display={{ base: "block", md: "none" }}
                  zIndex={10}
                >
                  <MultiDropdown
                    search={{ visible: true, placeholder: "select type" }}
                    selectedOptions={selectedOptions}
                    onDropdownChange={handleDropdownChange}
                    dropdowns={transformedOptions}
                    onApply={handleApply}
                    searchValue={search}
                    onSearchChange={handleSearchChange}
                    resetFilters={resetFilters}
                    dateFrom={startDate}
                    dateTo={endDate}
                    setFromDate={setStartDate}
                    setToDate={setEndDate}
                    handleGet={getLevelData}
                    isTable={false}
                  />
                </Box>
              )}
          </Box>

          {loading || !workFlowData ? (
            <Grid
              templateColumns={{ md: "1fr 1fr 1fr 1fr 1fr" }}
              gap={{ base: 5, lg: 8 }}
            >
              {[1, 2, 3, 4, 5].map((_, i) => (
                <Box
                  key={i}
                  bg="gray.100"
                  boxShadow="md"
                  p="1.125rem 1.375rem"
                  rounded={8}
                >
                  <Flex justify="center">
                    <Skeleton w="90%" h="15px" />
                  </Flex>
                  <Flex justify="center" mt={3}>
                    <Skeleton w={5} h={5} rounded="full" />
                  </Flex>
                </Box>
              ))}
            </Grid>
          ) : (
            <Grid
              templateColumns={{
                md: `repeat(${(accountDetails?.level === "level-1" ||
                  accountDetails?.level === "admin") &&
                  workflowProcess?.processName !== "foreign"
                  ? 5
                  : accountDetails?.level === "level-1" ||
                    accountDetails?.level === "admin"
                    ? 4
                    : 3
                  }, 1fr)`,
              }}
              gap={{ base: 5, lg: 8 }}
            >
              {[
                ...((accountDetails?.level === "level-1" ||
                  accountDetails?.level === "admin") &&
                  workflowProcess?.processName !== "foreign"
                  ? [
                    {
                      title: "Pending To Submit",
                      count: exceptionalCount,
                      bg: "bg-c-blue",
                      link: () => {
                        localStorage.setItem("documentTab", "3");
                        setActivePage(routePath.Approvals);
                      },
                    },
                  ]
                  : []),

                {
                  title: "PENDING",
                  count: pendingData,
                  bg: "bg-c-yellow",
                  link: () => {
                    localStorage.setItem("documentTab", "0");
                    setActivePage(routePath.Approvals);
                  },
                },

                {
                  title:
                    accountDetails?.role === "uploader"
                      ? "UPLOADED"
                      : "APPROVED",
                  count: approvedData,
                  bg: "bg-c-green",
                  link: () => {
                    localStorage.setItem("documentTab", "1");
                    setActivePage(routePath.Approvals);
                  },
                },

                {
                  title: "REJECTED",
                  count: rejectedData,
                  bg: "bg-c-red",
                  link: () => {
                    localStorage.setItem("documentTab", "2");
                    setActivePage(routePath.Approvals);
                  },
                },

                ...(accountDetails?.level === "level-1" ||
                  accountDetails?.level === "admin"
                  ? [
                    {
                      title: "Duplicate",
                      count: duplicateDocument,
                      bg: "bg-duplicate",
                      link: () => {
                        setActivePage(routePath.duplicate);
                      },
                    },
                  ]
                  : []),
              ].map((item, i) => (
                <DashWidget
                  key={i}
                  title={item.title}
                  count={item.count}
                  link={item.link}
                  props={{ className: item.bg, cursor: "pointer" }}
                />
              ))}
            </Grid>

          )}
        </Box>
        <Text fontSize={{ base: "lg", lg: "xl" }} fontWeight="700">
          Document Analytics
        </Text>

        {loading || !workFlowData ? (
          <Flex justify="center" align="center" mt="10%">
            <Spinner color="teal.400" />
          </Flex>
        ) : (
          <Card boxShadow="md" bg="white" p="1rem" rounded={8}>
            <Tabs
              variant="unstyled"
              index={tabIndex}
              onChange={handleChangeTabIndex}
              overflowX="auto"
            >
              <Box overflow="auto" className="customScrollBar">
                <TabList>
                  {accountDetails.level === "admin"
                    ? transformedTab.map((name: string, i: number) => (
                      <Tab
                        key={i}
                        fontWeight="700"
                        color="teal.300"
                        _selected={{ bg: "teal.300", color: "white" }}
                        _hover={{ bg: "teal.50" }}
                      >
                        {name}
                      </Tab>
                    ))
                    : transformedTab.map((name: string, i: number) => (
                      <Tab
                        key={i}
                        fontWeight="700"
                        color="teal.300"
                        _selected={{ bg: "teal.300", color: "white" }}
                        _hover={{ bg: "teal.50" }}
                        display={i <= userLevelIndex - 1 ? "flex" : "none"}
                      >
                        {name}
                      </Tab>
                    ))}
                </TabList>
              </Box>

              <TabPanels>
                {transformedTab.map((_: any, i: number) => (
                  <TabPanel key={i} p={0}>
                    <Grid
                      templateColumns={{ lg: "1fr 2fr" }}
                      gap={{ base: 5, lg: 8 }}
                    >
                      <Flex
                        order={{ base: 1, lg: 0 }}
                        justify="center"
                        h={{ md: "40vh" }}
                        overflow="hidden"
                      >
                        <PieChart chartData={chartData} />
                      </Flex>
                      <Box
                        overflowY="auto"
                        className="customScrollBar"
                        mt={{ base: "-.5rem", lg: "0" }}
                      >
                        <DashboardTable
                          invoiceData={chartData}
                          loading={loading}
                        />
                      </Box>
                    </Grid>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Card>
        )}
      </Grid>

      <WorkflowDocumentCount
        level={accountDetails?.level}
        isOpen={showWorkflowDocument}
        onClose={() => setOpenWorkflowDocument(false)}
        getDefaultWorkflow={getDefaultWorkFlow}
      />
      <WorkflowListModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        menu={false}
      />
      <UploadDocumentModal
        isOpen={isUploadOpen}
        onClose={handleCloseUploadModal}
        type="upload"
        handleGet={getLevelData}
      />
      {openForeignDrawer && <ForeignProcess name="Pending Approval" isOpen={openForeignDrawer} onClose={() => setOpenForeignDrawer(false)} />}
    </>
  );
}
